import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  listAdminTickets, replyTicket, closeTicket, listQuoteRequests, updateQuoteRequest,
} from "@/lib/adminApi";
import { apiErrorText, awaitingAgeHours, formatAge, isSlaWarning } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const TICKET_STATUS = { open: "Açık", answered: "Yanıtlandı", closed: "Kapalı" };
const QUOTE_STATUS = { new: "Yeni", contacted: "İletişime geçildi", won: "Kazanıldı", lost: "Kaybedildi" };
const SCOPE = { full: "Tam paket", items: "Seçili kalemler" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "");

function TicketsTab() {
  const [status, setStatus] = useState("all");
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setTickets(await listAdminTickets(status === "all" ? undefined : status));
    } catch (e) {
      toast.error(apiErrorText(e, "Talepler yüklenemedi"));
    }
  }, [status]);
  useEffect(() => { setTickets(null); load(); }, [load]);

  const current = (tickets || []).find((t) => t.id === openId) || null;

  const doReply = async () => {
    if (!reply.trim() || !current) return;
    setBusy(true);
    try {
      await replyTicket(current.id, reply.trim());
      setReply("");
      toast.success("Yanıt gönderildi");
      await load();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally { setBusy(false); }
  };
  const doClose = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await closeTicket(current.id);
      toast.success("Talep kapatıldı");
      await load();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div>
        <div className="mb-3 w-48">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {tickets === null && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
        {tickets && tickets.length === 0 && <p className="text-sm text-muted-foreground">Talep yok.</p>}
        <ul className="space-y-2">
          {(tickets || []).map((t) => (
            <li key={t.id}>
              <button
                onClick={() => setOpenId(t.id)}
                data-testid="ticket-row"
                className={`w-full rounded-md border p-3 text-left hover:bg-secondary ${t.id === openId ? "bg-secondary" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{t.subject}</span>
                  <Badge variant="outline">{TICKET_STATUS[t.status] || t.status}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t.email} · {fmt(t.created_at)}
                  {isSlaWarning(t) && (
                    <span className="ml-2 font-semibold text-red-600">SLA: {formatAge(awaitingAgeHours(t))} yanıtsız</span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        {!current && <p className="text-sm text-muted-foreground">Okumak için bir talep seçin.</p>}
        {current && (
          <div className="rounded-md border p-4" data-testid="ticket-detail">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-semibold">{current.subject}</h3>
              <Badge variant="outline">{TICKET_STATUS[current.status] || current.status}</Badge>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              {current.email}
              {current.finding_code ? ` · ${current.finding_code}` : ""}
              {current.token && (
                <> · <a className="underline" href={`/rapor/${current.token}`} target="_blank" rel="noreferrer">Raporu aç</a></>
              )}
            </p>
            <div className="mb-3 max-h-80 space-y-2 overflow-y-auto">
              {(current.messages || []).map((m, i) => (
                <div key={i} className={`rounded-md p-2 text-sm ${m.from === "expert" ? "ml-6 bg-emerald-50" : "mr-6 bg-secondary"}`}>
                  <div className="mb-0.5 text-[11px] text-muted-foreground">
                    {m.from === "expert" ? "Uzman" : "Müşteri"} · {fmt(m.at)}
                  </div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
            </div>
            {current.status !== "closed" && (
              <>
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder="Yanıtınız (müşteriye e-posta ile bağlantı gider, içerik gitmez)"
                  data-testid="ticket-reply-input"
                />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={doReply} disabled={busy || !reply.trim()} data-testid="ticket-reply-send">Yanıtla</Button>
                  <Button size="sm" variant="outline" onClick={doClose} disabled={busy} data-testid="ticket-close">Kapat</Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuoteRow({ q, onSaved }) {
  const [status, setStatus] = useState(q.status);
  const [note, setNote] = useState(q.note || "");
  const [busy, setBusy] = useState(false);
  const dirty = status !== q.status || note !== (q.note || "");
  const save = async () => {
    setBusy(true);
    try {
      await updateQuoteRequest(q.id, { status, note });
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally { setBusy(false); }
  };
  return (
    <TableRow data-testid="quote-row">
      <TableCell className="align-top text-xs">{fmt(q.created_at)}</TableCell>
      <TableCell className="align-top text-sm">
        <div className="font-medium">{q.name}</div>
        <div className="text-xs text-muted-foreground">{q.company}</div>
        <a className="text-xs underline" href={`mailto:${encodeURIComponent(String(q.email || "").split("?")[0])}`}>{q.email}</a>
        {q.phone && <div className="text-xs">{q.phone}</div>}
      </TableCell>
      <TableCell className="align-top text-xs">
        <div>{SCOPE[q.scope] || q.scope}</div>
        {(q.items || []).length > 0 && <div className="text-muted-foreground">{q.items.join(", ")}</div>}
        {q.report_token && (
          <a className="underline" href={`/rapor/${q.report_token}`} target="_blank" rel="noreferrer">Rapor</a>
        )}
      </TableCell>
      <TableCell className="max-w-xs align-top whitespace-pre-wrap text-xs">{q.message}</TableCell>
      <TableCell className="align-top">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="align-top">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} placeholder="Not" className="min-w-40 text-xs" />
        <Button size="sm" className="mt-1" onClick={save} disabled={!dirty || busy}>Kaydet</Button>
      </TableCell>
    </TableRow>
  );
}

function QuotesTab() {
  const [quotes, setQuotes] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    try {
      setQuotes(await listQuoteRequests(filter === "all" ? undefined : filter));
    } catch (e) {
      toast.error(apiErrorText(e, "Başvurular yüklenemedi"));
    }
  }, [filter]);
  useEffect(() => { setQuotes(null); load(); }, [load]);
  return (
    <div>
      <div className="mb-3 w-48">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            {Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {quotes === null && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {quotes && quotes.length === 0 && <p className="text-sm text-muted-foreground">Başvuru yok.</p>}
      {quotes && quotes.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead><TableHead>İletişim</TableHead><TableHead>Kapsam</TableHead>
                <TableHead>Mesaj</TableHead><TableHead>Durum</TableHead><TableHead>Not</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => <QuoteRow key={q.id + q.status + (q.note || "")} q={q} onSaved={load} />)}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  useNoindex();
  return (
    <main className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="mb-4 text-xl font-semibold">Gelen kutusu</h1>
      <Tabs defaultValue="tickets">
        <TabsList>
          <TabsTrigger value="tickets" data-testid="inbox-tab-tickets">Destek talepleri</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="inbox-tab-quotes">Teklif başvuruları</TabsTrigger>
        </TabsList>
        <TabsContent value="tickets"><TicketsTab /></TabsContent>
        <TabsContent value="quotes"><QuotesTab /></TabsContent>
      </Tabs>
    </main>
  );
}
